import { defineComponent as _defineComponent } from 'vue'
import { chunk, Dictionary, find, uniqBy } from 'lodash';
import { ProductConfiguratorMultiColorProperty, store } from '@/store';
import ClipboardService from '@/core/clipboard.service';
import ProductInformation from '../ProductInformation.vue';
import MultiColorViewObject = Vertica.LouisPoulsen.Application.Content.Pages.Product.MultiColorViewObject;
import useBreakpoints from '@/core/responsive/breakpoints/breakpoint.mixin';
import IconCopy from '@/ui/icons/IconCopy.vue';
import Vue, { computed, getCurrentInstance, onMounted, ref } from 'vue';

interface configuratorOption {
    code: string;
    text: string;
    helpText: string;
}

interface PropertyValuePair {
    property: string;
    value: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'VariantConfigurator',
  props: {
    hiddenProperties: { default: () => [] },
    allowNonStandardVariants: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props as { hiddenProperties: string[], allowNonStandardVariants: boolean }



const textColor = computed<OpacityFunction>(() => store.getters['app/textColor']);
const product = computed<ProductBaseViewModel<ProfessionalVariantViewObject>>(() => store.getters['catalog/product']);
const productVariants = computed<ProfessionalVariantViewObject[]>(() => store.getters['catalog/productVariants']);
const productConfiguratorProperties = computed<VariantConfiguratorOptionsViewObject[]>(() => store.getters['catalog/productConfiguratorProperties']);

function selectVariantConfig(variant: VariantBaseViewObject, pushToHistory?: boolean) {
    store.dispatch('catalog/selectVariantConfig', { variant, pushToHistory });
}
function toggleShowVariantDataSpecs(flag: boolean) {
    store.dispatch('catalog/toggleShowVariantDataSpecs', flag);
}

const configOptionsThreshold = 3;
const fullConfigOptionsVisibleType = ref<VariantConfiguratorOptionsViewObject | null>(null);
const pendingfullConfigOptionsFilterChange = ref<PropertyValuePair | null>(null);

const selectedOptions = ref<{ [key: string]: string }>({});

const configuredVariant = ref<ProfessionalVariantViewObject>(null);
const defaultVariant = ref<ProfessionalVariantViewObject>(null);
const showFullConfigOptionsModal = ref<boolean>(false);

const autoUpdatedProperties = ref<Dictionary<string>>({});
let autoUpdatedPropertiesTimer = 0;
const autoUpdatedPropertiesTiming = 400;

onMounted(setDefaultVariant);

const specificationText = computed<string>(() => {
    return product.value.productName + ', ' +
           Object.values(selectedOptionsForQuote.value).filter(x => x != null).join(', ');
});

async function copySpecificationText() {
    await ClipboardService.copyToClipboard(specificationText.value);
}

const productConfiguratorPropertiesToShow = computed<Array<VariantConfiguratorOptionsViewObject>>(() => {
    return productConfiguratorProperties.value.filter(prop => !props.hiddenProperties.includes(prop.key));
});

const singleValueProperties = computed<Array<VariantConfiguratorOptionsViewObject>>(() => {
    return productConfiguratorPropertiesToShow.value.filter(prop => productConfiguratorPropertyOptions(prop.key).length === 1);
});

const showMultiValuePropertyRegardlessOfNumberOfOptions = ['lightsourceText'];
const multiValueProperties = computed<Array<VariantConfiguratorOptionsViewObject>>(() =>
    productConfiguratorPropertiesToShow.value.filter(prop => {
        const options = productConfiguratorPropertyOptions(prop.key);
        return options.length > 1 || (options.length > 0 && showMultiValuePropertyRegardlessOfNumberOfOptions.indexOf(prop.key) > -1);
    }));

function productConfiguratorPropertyOptions(type: string) {
    return uniqBy(productVariants.value.filter(v => v[type] !== null), type);
}

const productConfiguratorMultiColorOptions = computed<MultiColorViewObject[]>(() => {
    const variants = productVariants.value.filter(v => v[ProductConfiguratorMultiColorProperty] && v[ProductConfiguratorMultiColorProperty].length);
    let options = [];

    variants.forEach(v => {
        options = options.concat(v[ProductConfiguratorMultiColorProperty]);
    });
    return uniqBy(options, 'code');
});

function select(key: string, value: string) {
    selectedOptions.value[key] = value;
    const bestVariantCandidate = setBestVariantCandidate({ property: key, value });
    if (!props.allowNonStandardVariants) {
        updateConfiguredVariant(bestVariantCandidate);
    } else {
        configuredVariant.value = bestVariantCandidate;
    }
}

const selectedOptionsForQuote = computed<{ [key: string]: string }>(() => {
    const options = {};
    for (const key of Object.keys(selectedOptions.value)) {
        const code = selectedOptions.value[key];
        const text = key === ProductConfiguratorMultiColorProperty
            ? productConfiguratorMultiColorOptions.value.find(option => option.code === code).text
            : code;

        options[key] = text;
    }
    return options;
});

function getConfiguratorOptions(property: string): (ProfessionalVariantViewObject | MultiColorViewObject)[] {
    if (property === ProductConfiguratorMultiColorProperty) {
        return productConfiguratorMultiColorOptions.value;
    } else {
        return productConfiguratorPropertyOptions(property);
    }
}

function dropdownDisplayText(propertyKey: string) : string {
    const option = fullConfiguratorOptions(propertyKey).find(x => x.code === selectedOptions.value[propertyKey]);
    return option ? option.text : '';
}

function defaultConfiguratorOptions(property: VariantConfiguratorOptionsViewObject) : (ProfessionalVariantViewObject | MultiColorViewObject)[] {
    const currentInstance = getCurrentInstance();
    const $options = currentInstance.proxy.$options;
    return $options.filters.configOptions(getConfiguratorOptions(property.key), property.key, selectedOptionCode(property.key), breakpointRelatedThreshold.value);
}

function desktopConfiguratorOptions(property: VariantConfiguratorOptionsViewObject) : configuratorOption[] {
    return defaultConfiguratorOptions(property).map(x => convertToConfiguratorOption(property.key, x));
}

function fullConfiguratorOptions(propertyKey: string): configuratorOption[] {
    return getConfiguratorOptions(propertyKey).map(x => convertToConfiguratorOption(propertyKey, x));
}

function configurationOptionColumns(propertyKey: string) {
    return chunk(fullConfiguratorOptions(propertyKey), 2);
}

function convertToConfiguratorOption(propertyKey: string, obj: ProfessionalVariantViewObject | MultiColorViewObject) : configuratorOption {
    return 'code' in obj
        ? {
            code: obj.code,
            text: obj.text,
            helpText: obj.helpText
        }
        : {
            code: obj[propertyKey],
            text: obj[propertyKey],
            helpText: ''
        };
}

function showSpecsOverlay() {
    if (configuredVariant.value) {
        selectVariantConfig(configuredVariant.value, true);
        toggleShowVariantDataSpecs(true);
    }
}

function setBestVariantCandidate(choice: PropertyValuePair) : ProfessionalVariantViewObject | null {
    let candidates;
    const property = choice.property;
    const value = choice.value;
    if (property === ProductConfiguratorMultiColorProperty) {
        candidates = productVariants.value.filter(v => {
            return find(v[ProductConfiguratorMultiColorProperty], { code: value });
        });
    } else {
        candidates = productVariants.value.filter(v => v[property] === value);
    }

    Object.keys(selectedOptions.value).forEach(key => {
        let tempNextCandidates;
        if (key === ProductConfiguratorMultiColorProperty) {
            tempNextCandidates = candidates.filter(v => {
                return find(v[ProductConfiguratorMultiColorProperty], { code: selectedOptionCode(key) });
            });
        } else {
            tempNextCandidates = candidates.filter(v => v[key] === selectedOptionCode(key));
        }
        candidates = props.allowNonStandardVariants || tempNextCandidates.length > 0 ? tempNextCandidates : candidates;
    });

    return candidates.length > 0 ? candidates[0] : null;
}

function updateConfiguredVariant(variant: ProfessionalVariantViewObject, updateAutoUpdatedProperties = true):void {
    configuredVariant.value = variant;
    productConfiguratorPropertiesToShow.value.forEach(property => {
        let setAutoUpdate = false;
        let value;
        if (property.key === ProductConfiguratorMultiColorProperty) {
            const hasSelectedColor = !!find(variant[ProductConfiguratorMultiColorProperty], { code: selectedOptionCode(ProductConfiguratorMultiColorProperty) }) as boolean;
            const selectedMultiColor = hasSelectedColor
                ? selectedOptionCode(ProductConfiguratorMultiColorProperty)
                : (find(variant[ProductConfiguratorMultiColorProperty], { default: true }) as MultiColorViewObject).code;
            value = selectedMultiColor;
            setAutoUpdate = !hasSelectedColor && updateAutoUpdatedProperties;
        } else {
            value = variant[property.key];
            setAutoUpdate = updateAutoUpdatedProperties && variant[property.key] !== selectedOptions.value[property.key];
        }
        Vue.set(selectedOptions.value, property.key, value);
        if (setAutoUpdate) {
            autoUpdatedProperties.value[property.key] = value;
        }
    });
    if (updateAutoUpdatedProperties) {
        clearTimeout(autoUpdatedPropertiesTimer);
        autoUpdatedPropertiesTimer = setTimeout(() => {
            autoUpdatedProperties.value = {};
        }, autoUpdatedPropertiesTiming);
    }
}

function isAffectingOtherOptions(property: string, option: configuratorOption): boolean {
    return !isInValidProductConfiguratorOption({ property, value: option.code });
}

function isInValidProductConfiguratorOption(choice: PropertyValuePair) {
    if (!configuredVariant.value) {
        return false;
    }
    const type = choice.property;
    const value = choice.value;
    const variantsWithTypeAndValuePair = productVariants.value.filter(v => {
        if (type !== ProductConfiguratorMultiColorProperty) {
            return v[type] === value;
        } return !!find(v[ProductConfiguratorMultiColorProperty], { code: value });
    });
    if (!variantsWithTypeAndValuePair.length) { return false; }

    const notHiddenProperties = productConfiguratorProperties.value.filter(prop => !props.hiddenProperties.includes(prop.key));
    return variantsWithTypeAndValuePair.some(v => notHiddenProperties.every(prop => {
        if (prop.key === type) { return true; }
        if (prop.key !== ProductConfiguratorMultiColorProperty) {
            const res = v[prop.key] === configuredVariant.value[prop.key];
            return res;
        }
        return !!find(v[ProductConfiguratorMultiColorProperty], { code: selectedOptionCode(ProductConfiguratorMultiColorProperty) }) as boolean;
    }));
}

function selectedOptionCode(propertyKey: string) : string | undefined {
    return selectedOptions.value[propertyKey];
}

function setDefaultVariant() {
    const defaultVariantValue = productVariants.value.find(v => v.isDefault) || productVariants.value[0];
    defaultVariant.value = defaultVariantValue;
    updateConfiguredVariant(defaultVariantValue, false);
}

function hasConfigOptionsOverflow(type:string):boolean {
    return getConfiguratorOptions(type).length > breakpointRelatedThreshold.value;
}

function showFullConfigOptions(configOption: VariantConfiguratorOptionsViewObject):void {
    showFullConfigOptionsModal.value = true;
    fullConfigOptionsVisibleType.value = configOption;
    pendingfullConfigOptionsFilterChange.value = { property: configOption.key, value: selectedOptions.value[configOption.key] };
}

function hideFullConfigOptions(): void {
    showFullConfigOptionsModal.value = false;
}

function submitPendingfullConfigOptionsFilterChange(): void {
    select(pendingfullConfigOptionsFilterChange.value.property, pendingfullConfigOptionsFilterChange.value.value);
    hideFullConfigOptions();
}

const breakpointIsActive = useBreakpoints();

const breakpointRelatedThreshold = computed<number>(() => {
    return breakpointIsActive.value.small ? 999 : configOptionsThreshold;
});


return { __sfc: true,props, textColor, product, productVariants, productConfiguratorProperties, selectVariantConfig, toggleShowVariantDataSpecs, configOptionsThreshold, fullConfigOptionsVisibleType, pendingfullConfigOptionsFilterChange, selectedOptions, configuredVariant, defaultVariant, showFullConfigOptionsModal, autoUpdatedProperties, autoUpdatedPropertiesTimer, autoUpdatedPropertiesTiming, specificationText, copySpecificationText, productConfiguratorPropertiesToShow, singleValueProperties, showMultiValuePropertyRegardlessOfNumberOfOptions, multiValueProperties, productConfiguratorPropertyOptions, productConfiguratorMultiColorOptions, select, selectedOptionsForQuote, getConfiguratorOptions, dropdownDisplayText, defaultConfiguratorOptions, desktopConfiguratorOptions, fullConfiguratorOptions, configurationOptionColumns, convertToConfiguratorOption, showSpecsOverlay, setBestVariantCandidate, updateConfiguredVariant, isAffectingOtherOptions, isInValidProductConfiguratorOption, selectedOptionCode, setDefaultVariant, hasConfigOptionsOverflow, showFullConfigOptions, hideFullConfigOptions, submitPendingfullConfigOptionsFilterChange, breakpointIsActive, breakpointRelatedThreshold, ProductInformation, IconCopy }
}

})